import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'
import {AssigneePickerBase, type AssigneePickerBaseProps} from '@github-ui/item-picker-assignee'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchAssignees,
  buildAssigneePickerSearchQueryKey,
  transformSearchAssigneeResponseToQueryData,
  type AssigneePickerQueryData,
} from './api/api-get-search-assignees'
import type {Assignee} from './api/assignee'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {getActorCapabilities} from './utils/assignee-picker'

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== AssigneePicker (exported)
//                                                                              <== AssigneeRepositoryPicker (exported)

export type AssigneePickerAssignee = Assignee

export async function fetchAssignees(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    first,
    number,
    loginNames,
    capabilities,
  }: {
    owner: string
    repo: string
    query: string
    first?: number
    number?: number
    loginNames?: string[]
    capabilities?: string[]
  },
): Promise<AssigneePickerQueryData> {
  // If loginNames are provided and first is not explicitly set, set first to the number of loginNames
  let effectiveFirst: number | undefined
  if (first !== undefined) {
    effectiveFirst = first
  } else if (loginNames !== undefined) {
    effectiveFirst = loginNames.length
  } else {
    effectiveFirst = undefined
  }
  // Convert loginNames array to comma-separated string for the API
  const loginNamesString = loginNames !== undefined ? loginNames.join(',') : undefined

  const response = await queryClient.fetchQuery({
    queryKey: buildAssigneePickerSearchQueryKey({
      owner,
      repo,
      query,
      first: effectiveFirst,
      number,
      loginNames: loginNamesString,
      capabilities,
    }),
    queryFn: async () => {
      const responseJson = await apiGetSearchAssignees({
        owner,
        repo,
        query,
        first: effectiveFirst,
        number,
        loginNames: loginNamesString,
        capabilities,
      })
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchAssigneeResponseToQueryData(responseData.data)
    },
  })
  return response
}

export type AssigneePickerProps = Omit<AssigneePickerBaseProps, 'searchAssignees' | 'viewer' | 'isLoading'> & {
  repo: string
  owner: string
  number?: number
  viewer: AssigneePickerAssignee | null
}

export type AssigneeRepositoryPickerProps = Omit<
  AssigneePickerBaseProps,
  'searchAssignees' | 'viewer' | 'isLoading'
> & {
  repo: string
  owner: string
  assigneeTokens: string[]
  includeAssignableBots: boolean
  includeAuthorableBots: boolean
  viewer: AssigneePickerAssignee | null
}

function ItemPickerFetcherForIssue({
  repo,
  owner,
  number,
  assignees,
  suggestions,
  viewer,
  ...rest
}: AssigneePickerProps) {
  const [isLoading, setIsLoading] = useState(!suggestions || suggestions.length === 0)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<AssigneePickerQueryData | null>(null)
  const queryClient = useQueryClient()

  const hasPreloadedData = (suggestions?.length || 0) > 0

  useEffect(() => {
    if (!IS_SERVER && !hasPreloadedData) {
      const fetchInitialAssignees = async () => {
        try {
          const response = await fetchAssignees(queryClient, {owner, repo, query: '', number, first: 10})
          setData(response)
          setIsLoading(false)
          setIsError(false)
        } catch {
          setIsError(true)
          setIsLoading(false)
        }
      }
      fetchInitialAssignees()
    }
  }, [fetchKey, owner, queryClient, repo, number, hasPreloadedData])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('assignees'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const searchAssignees = useCallback(
    async (query: string) => {
      try {
        setIsLoading(true)
        const response = await fetchAssignees(queryClient, {owner, repo, query, number, first: 10})
        setIsLoading(false)
        setIsError(false)
        return response.assignees
      } catch {
        setIsLoading(false)
        setIsError(true)
        return []
      }
    },
    [queryClient, owner, repo, number],
  )

  const suggestedActors = suggestions || data?.assignees || []

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <AssigneePickerBase
      {...rest}
      assignees={assignees}
      suggestions={suggestedActors}
      isLoading={isLoading}
      searchAssignees={searchAssignees}
      viewer={viewer}
    />
  )
}
function ItemPickerFetcherForRepository({
  repo,
  owner,
  assignees,
  assigneeTokens,
  suggestions,
  includeAssignableBots,
  includeAuthorableBots,
  maximumAssignees,
  viewer,
  ...rest
}: AssigneeRepositoryPickerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [data, setData] = useState<AssigneePickerQueryData | null>(null)
  const [suggestedAssignees, setSuggestedAssignees] = useState<Assignee[] | null>(null)
  const [totalAssignableUsers, setTotalAssignableUsers] = useState<number | null>(null)
  const queryClient = useQueryClient()

  const capabilities = useMemo(
    () => getActorCapabilities({includeAssignableBots, includeAuthorableBots}),
    [includeAssignableBots, includeAuthorableBots],
  )

  const shouldFetchFromAssigneeTokens = assigneeTokens.length > 0
  useEffect(() => {
    if (!IS_SERVER && shouldFetchFromAssigneeTokens) {
      const fetchData = async () => {
        try {
          setIsLoading(true)
          const response = await fetchAssignees(queryClient, {
            owner,
            repo,
            query: '',
            loginNames: assigneeTokens,
            capabilities,
          })
          setData(response)
          setIsLoading(false)
          setIsError(false)
        } catch {
          setIsLoading(false)
          setIsError(true)
        }
      }
      fetchData()
    }
  }, [fetchKey, owner, repo, assigneeTokens, capabilities, queryClient, shouldFetchFromAssigneeTokens])

  const searchAssignees = useCallback(
    async (query: string) => {
      if (totalAssignableUsers && totalAssignableUsers <= VALUES.maximumSuggestedUsers && suggestedAssignees) {
        setIsLoading(false)
        return fuzzyFilter<Assignee>({
          items: [...suggestedAssignees, ...(rest.noAssigneeOption ? [SPECIAL_VALUES.noAssigneeData as Assignee] : [])],
          filter: query,
          key: (l: Assignee) =>
            (l.isAgent ? l.displayName || l.login : l.login) || (rest.noAssigneeOption?.source?.login ?? ''),
          secondaryKey: (l: Assignee) => `${l.name}`,
        })
      }

      try {
        setIsLoading(true)
        const response = await fetchAssignees(queryClient, {
          owner,
          repo,
          query,
          first: VALUES.maximumSuggestedUsers,
          capabilities,
        })
        const assignableActors = response.assignees

        // Store total count when query is empty
        if (response.totalCount && query.trim() === '') {
          setTotalAssignableUsers(response.totalCount)
        }

        if (rest.noAssigneeOption && hasFuzzyMatch(query, rest.noAssigneeOption?.source?.login ?? '')) {
          assignableActors.unshift({...SPECIAL_VALUES.noAssigneeData} as Assignee)
        }

        setIsLoading(false)
        setIsError(false)
        return assignableActors
      } catch {
        setIsLoading(false)
        setIsError(true)
        return []
      }
    },
    [totalAssignableUsers, suggestedAssignees, rest.noAssigneeOption, queryClient, owner, repo, capabilities],
  )

  const suggestedActors: Assignee[] = useMemo(() => {
    let all: Assignee[] = []
    if (assignees && assignees.length > 0) {
      all = assignees
    } else if (assigneeTokens.length > 0) {
      all = data?.assignees || []
    }

    if (rest.noAssigneeOption?.selected) {
      all.unshift(SPECIAL_VALUES.noAssigneeData as Assignee)
    }

    return all
  }, [assigneeTokens.length, assignees, data?.assignees, rest.noAssigneeOption?.selected])

  useEffect(() => {
    const fetchSuggestedAssignees = async () => {
      const result = await searchAssignees('')
      setSuggestedAssignees(result)
      setIsLoading(false)
    }

    fetchSuggestedAssignees()
    // disabling this as we only want to fetch the suggested assignees once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const suggestedItems = useMemo(() => {
    const copilot = (suggestedAssignees || []).find(a => a.isCopilot)
    // create a new array instance because if we need to insert copilot
    // we need a new instance to force a re-render of the child component (AssigneePickerBase)
    const source = [...(suggestions ? suggestions : suggestedAssignees || [])]

    if (copilot && !source.find(a => a.isCopilot)) {
      source.unshift(copilot)
    }

    return source
  }, [suggestedAssignees, suggestions])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('assignees'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <AssigneePickerBase
      {...rest}
      assignees={suggestedActors}
      suggestions={suggestedItems}
      isLoading={isLoading}
      searchAssignees={searchAssignees}
      maximumAssignees={maximumAssignees}
      viewer={viewer}
    />
  )
}

const getKeyBindingCommandId = (name?: string) =>
  name === 'author' ? 'item-pickers:open-author' : 'item-pickers:open-assignees'

export function AssigneePicker({
  repo,
  owner,
  number,
  anchorElement,
  readonly,
  assignees,
  suggestions,
  viewer,
  ...rest
}: AssigneePickerProps & {number: number}) {
  return (
    <LazyItemPicker
      keybindingCommandId={getKeyBindingCommandId(rest.name)}
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcherForIssue
          repo={repo}
          owner={owner}
          number={number}
          readonly={readonly}
          assignees={assignees}
          suggestions={suggestions}
          viewer={viewer}
          triggerOpen
          anchorElement={anchorElement}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}

export function AssigneeRepositoryPicker({anchorElement, ...props}: AssigneeRepositoryPickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      keybindingCommandId={getKeyBindingCommandId(props.name)}
      createChild={() => <ItemPickerFetcherForRepository anchorElement={anchorElement} triggerOpen {...props} />}
      insidePortal={props.insidePortal}
    />
  )
}

try{ ItemPickerFetcherForIssue.displayName ||= 'ItemPickerFetcherForIssue' } catch {}
try{ ItemPickerFetcherForRepository.displayName ||= 'ItemPickerFetcherForRepository' } catch {}
try{ AssigneePicker.displayName ||= 'AssigneePicker' } catch {}
try{ AssigneeRepositoryPicker.displayName ||= 'AssigneeRepositoryPicker' } catch {}